import { Block, useTelemetry } from '@snowflake/core-ui';
import {
  AwsMarketplaceType,
  Cloud,
  AwsMpAccount,
  SignupFlowType,
  FeatureFlag,
  SignupEventType,
  isCortexCodeFlow,
} from '@signup/shared';
import { SignupEventName } from '../utils/SignupLogger';
import queryString from 'query-string';
import * as React from 'react';
import { v4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import { AwsErrorCard } from '../components/Cards/AwsErrorCard';
import { AwsCard } from '../components/Cards/forms/AwsCard';
import { AzureCard } from '../components/Cards/forms/AzureCard';
import { GcpCard } from '../components/Cards/forms/GcpCard';
import { SignupCard } from '../components/Cards/forms/SignupCard/SignupCard';
import { PageNotFoundCard } from '../components/Cards/PageNotFoundCard';
import { CertificationsFooter } from '../components/CertificationsFooter';
import { Footer } from '../components/Footer';
import { Header } from '../components/Header';
import { InfoList } from '../components/MarketingText/InfoList';
import { Title } from '../components/MarketingText/Title';
import { InternalHeaders } from '../utils/InternalHeaders';
import { useSignupPageContext } from './SignupContext';
import { MarketingParams, SignupParams, parseQueryParams } from '../utils/ParseQueryParams';
import { useListingInformation } from './UseListingInformation';
import * as configs from '../utils/configurations';
import { getAwsMarketplaceType } from '../utils/signupFlowTypeUtils';
import { SignupCardV2 } from '../components/Cards/forms/SignupCardV2/SignupCardV2';
import { SignupCardV2Modified } from '../components/Cards/forms/SignupCardV2/SignupCardV2Modified';
import { MarketingTextType } from '../types';
import { useSignupFlowType } from '../utils/useSignupFlowType';
import { CardMarketingTitle } from '../components/MarketingText/CardMarketingTitle';
import {
  UnifiedSignupContainer,
  ViewContent,
  ViewMode,
} from '../components/UnifiedSignupContainer';
import { CortexLeftContent } from '../components/MarketingText/CortexLeftContent';
import { CortexSignupContainer } from '../components/CortexSignupContainer';
import DefaultLeftContent from '../components/MarketingText/DefaultLeftContent';

const recaptchaRef = React.createRef();

interface SignupPageProps {
  formId: string;
  pageNotFound?: boolean;
}

export const SignupPage: React.FC<SignupPageProps> = props => {
  const location = useLocation();
  const { renderingOptions, modifiedSignupFlowExperimentGroup } = useSignupPageContext();
  const { logEvent } = useTelemetry();

  const signupFlowType = useSignupFlowType();
  const awsMarketPlaceType = getAwsMarketplaceType();

  const userCountry = configs.getIsoCode();
  const isUSUser = userCountry === 'US';
  // Cortex Code flows (CortexCodeCLI and CocoSubscriptionPromo) always use the modified flow,
  // regardless of region
  const isModifiedSignupFlowTreatment =
    isCortexCodeFlow(signupFlowType) ||
    (isUSUser && modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT');

  const [activeFormId, setActiveFormId] = React.useState(props.formId);
  const handleToggle = React.useCallback(
    (view: ViewMode) => {
      const newFormId = v4();
      setActiveFormId(newFormId);
      logEvent({
        event: SignupEventName.SIGNUP_UNIFIED_VIEW_SWITCH,
        type: SignupEventType.UI_RESPONSE_SUCCESS,
        data: {
          newFormId,
          viewShown: view === 'viewA' ? 'default' : 'cortex_code_cli',
        },
      });
    },
    [logEvent],
  );

  const [signupParams, marketingParams]: [SignupParams, MarketingParams] = React.useMemo(() => {
    return parseQueryParams(location?.search);
  }, [location?.search]);

  const { listingInformation, isListingInfoLoading } = useListingInformation(signupParams.listing);

  if (!!awsMarketPlaceType) signupParams.cloud = Cloud.AWS;

  const isAwsError = !!awsMarketPlaceType && !signupParams.ampt;

  const card = React.useMemo(() => {
    if (props.pageNotFound || signupFlowType === SignupFlowType.Unknown) {
      return <PageNotFoundCard />;
    } else if (isAwsError) {
      return <AwsErrorCard />;
    } else if (
      awsMarketPlaceType === AwsMarketplaceType.CAPACITY ||
      awsMarketPlaceType === AwsMarketplaceType.ON_DEMAND_ADD_SUBSCRIPTION
    ) {
      return (
        <AwsCard
          signupParams={signupParams}
          marketingParams={marketingParams}
          formId={activeFormId}
          recaptchaRef={recaptchaRef}
          awsMpAccount={signupParams.awsmp || AwsMpAccount.SFC_INC_MARKETPLACE_US}
        />
      );
    } else if (signupFlowType === SignupFlowType.Azure) {
      return (
        <AzureCard
          signupParams={signupParams}
          marketingParams={marketingParams}
          formId={activeFormId}
          recaptchaRef={recaptchaRef}
          queryParams={queryString.parse(location?.search || '')}
        />
      );
    } else if (signupFlowType === SignupFlowType.Gcp) {
      return <GcpCard />;
    } else if (configs.usingNewSignupForm()) {
      if (isModifiedSignupFlowTreatment) {
        return (
          <SignupCardV2Modified
            signupParams={signupParams}
            marketingParams={marketingParams}
            formId={activeFormId}
            recaptchaRef={recaptchaRef}
          />
        );
      }
      return (
        <SignupCardV2
          signupParams={signupParams}
          marketingParams={marketingParams}
          formId={activeFormId}
          recaptchaRef={recaptchaRef}
        />
      );
    } else {
      return (
        <SignupCard
          signupParams={signupParams}
          marketingParams={marketingParams}
          formId={activeFormId}
          recaptchaRef={recaptchaRef}
        />
      );
    }
  }, [
    props.pageNotFound,
    activeFormId,
    location?.search,
    signupFlowType,
    awsMarketPlaceType,
    signupParams,
    marketingParams,
    isAwsError,
    isModifiedSignupFlowTreatment,
  ]);

  const marketingTextRequired = React.useMemo(
    () =>
      renderingOptions.marketingTextType !== MarketingTextType.OMIT &&
      !props.pageNotFound &&
      !isAwsError,
    [renderingOptions.marketingTextType, props.pageNotFound, isAwsError],
  );

  const renderLeftText = () => {
    if (marketingTextRequired) {
      return (
        <Block
          className="trial-left-container"
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          <Title listingInformation={listingInformation} isLoading={isListingInfoLoading} />
          <InfoList listingInformation={listingInformation} isLoading={isListingInfoLoading} />
        </Block>
      );
    }
  };

  const isCortexCodeCLI = isCortexCodeFlow(signupFlowType);
  const isDefault = signupFlowType === SignupFlowType.Default;

  // When a `t` token is present in the URL, collapse the toggle to the single
  // AI Data Cloud view and hide the Cortex Code option. Only applies on the
  // Default flow: on the Cortex path the flow is intrinsically Cortex, so we
  // leave its normal toggle intact rather than show AI Data Cloud branding over
  // a Cortex form. The token value is not validated: its presence alone drives
  // the single view.
  const isAiDataCloudOnly = isDefault && !!signupParams.t;

  // When a valid `program` param is present (e.g. ?program=scc_coco), show only the
  // CoCo tab and hide AI Data Cloud. The server already sets signupFlowType to
  // CocoSubscriptionPromo for this case, so the CoCo card renders immediately.
  const isCoCoOnly = !!signupParams.program;

  if (
    configs.isFeatureEnabled(FeatureFlag.FF_TOGGLE_STANDALONE_COCO_PAGE) &&
    location?.pathname === '/cortex-code' &&
    isCortexCodeCLI
  ) {
    return (
      <CortexSignupContainer
        recaptchaRef={recaptchaRef}
        pageNotFound={props.pageNotFound}
        signupCard={card}
        signupParams={signupParams}
        marketingParams={marketingParams}
        formId={activeFormId}
      />
    );
  }

  if (
    configs.isFeatureEnabled(FeatureFlag.FF_ENABLE_SIGNUP_WITH_TOGGLE) &&
    (isCortexCodeCLI || isDefault)
  ) {
    // SocialLoginButtons are rendered inside SignupFormStepOneModified now,
    // so they have access to the live form state (edition/cloud/region/country)
    // and we don't need to render them as a sibling of the card.

    // Create default view content
    const defaultView: ViewContent = {
      label: { primary: 'AI Data Cloud', secondary: 'For Enterprise' },
      leftContent: <DefaultLeftContent />,
      rightContent: card, // Card updates reactively when flow type changes
      onActivate: () => configs.setSignupFlowType(SignupFlowType.Default),
    };

    const isCortexCodeV2 = configs.isFeatureEnabled(FeatureFlag.FF_ENABLE_CORTEX_CODE_2026);

    // Create cortex view content with explicit card logic
    const cortexView: ViewContent = {
      label: {
        primary: isCortexCodeV2 ? 'Snowflake CoCo' : 'Cortex Code CLI',
        secondary: 'For Developers',
      },
      leftContent: <CortexLeftContent />,
      rightContent: card,
      onActivate: () =>
        configs.setSignupFlowType(
          isCoCoOnly ? SignupFlowType.CocoSubscriptionPromo : SignupFlowType.CortexCodeCLI,
        ),
    };

    return (
      <UnifiedSignupContainer
        viewA={isCoCoOnly ? cortexView : defaultView}
        viewB={isCoCoOnly ? defaultView : cortexView}
        initialView={isCortexCodeCLI && !isCoCoOnly ? 'viewB' : 'viewA'}
        recaptchaRef={recaptchaRef}
        pageNotFound={props.pageNotFound}
        onToggle={handleToggle}
        singleView={isAiDataCloudOnly || isCoCoOnly}
      />
    );
  }

  // Default signup flow
  return (
    <Block className="trial-page-container">
      <InternalHeaders />
      <Header />
      <Block className="trial-responsive-flex-container">
        {renderLeftText()}
        <Block
          className="trial-right-container"
          display="flex"
          flexDirection="column"
          justifyContent="top"
          maxWidth="520px"
        >
          {marketingTextRequired && <CardMarketingTitle />}
          {card}
        </Block>
      </Block>
      {!marketingTextRequired && <CertificationsFooter />}
      <Footer recaptchaRef={recaptchaRef} pageNotFound={props.pageNotFound} />
    </Block>
  );
};
